import { Injectable, signal } from '@angular/core';

interface StoredUsername {
  username: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class UsernameStorageService {
  private readonly storageKey = 'snk.username';
  private readonly ttlMs = 24 * 60 * 60 * 1000;

  private readonly current = signal<string | null>(this.readValidUsername());

  /**
   * The stored username as a signal. Prefer this over `getUsername()` in
   * templates — the header used to read the value once and never updated after
   * a rename.
   */
  readonly username = this.current.asReadonly();

  saveUsername(username: string): void {
    const payload: StoredUsername = {
      username,
      expiresAt: Date.now() + this.ttlMs,
    };
    localStorage.setItem(this.storageKey, JSON.stringify(payload));
    this.current.set(username);
  }

  /** Reads the username and slides the TTL forward. */
  getUsername(): string | null {
    const username = this.readValidUsername();
    this.current.set(username);
    return username;
  }

  hasValidUsername(): boolean {
    return Boolean(this.getUsername());
  }

  clearUsername(): void {
    localStorage.removeItem(this.storageKey);
    this.current.set(null);
  }

  /** Storage read + expiry check + TTL refresh, without touching the signal. */
  private readValidUsername(): string | null {
    const stored = this.readStored();
    if (!stored) {
      return null;
    }

    if (Date.now() > stored.expiresAt) {
      localStorage.removeItem(this.storageKey);
      return null;
    }

    const refreshed: StoredUsername = {
      username: stored.username,
      expiresAt: Date.now() + this.ttlMs,
    };
    localStorage.setItem(this.storageKey, JSON.stringify(refreshed));

    return stored.username;
  }

  private readStored(): StoredUsername | null {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as StoredUsername;
      if (!parsed.username || !parsed.expiresAt) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
